// =============================================================================
// FEATURED-BLOG-HORIZON — Horizon `Component` that WRAPS the vendored classic Swiper engine.
// HORIZON PILOT #3: the featured-blog "From the Brew Crew" PEEK carousel reparented onto Horizon's
// `Component` base class (import { Component } from '@theme/component', resolved via the head importmap
// in snippets/horizon-importmap.liquid), loaded as native ESM through a section-scoped
// `<script type="module">` in sections/featured-blog-horizon.liquid — NOT the webpack src/js bundle.
//
// This element IS the `.featured-blog` root (`<featured-blog-horizon class="featured-blog …">`), so the
// SHARED src/scss/objects/global/_featured-blog.scss styles it identically to the original section. It
// reparents the peek-carousel init from src/js/objects/global/featured-blog-carousel.js (slidesPerView
// 'auto', fixed-width slides that peek, a single circular nav arrow, no dots) onto the Component
// lifecycle:
//   • connectedCallback  → wait for `window.Swiper`, then (FIX #1) count slides and SKIP init for ≤1;
//                          otherwise `new window.Swiper(this.refs.swiper, …)` (double-init guarded).
//   • disconnectedCallback → `this.#swiper.destroy()` so a theme-editor re-render leaves no zombie.
//
// INDEPENDENCE from the global src/js featured-blog-carousel.js (which stays for the ORIGINAL section):
//   • That module scans `.featured-blog__swiper.swiper`. This section's Swiper container is classed
//     `.featured-blog-horizon__swiper.swiper` (NOT `.featured-blog__swiper`), so the global scanner
//     never matches it — no double Swiper on one container.
//   • The section's schema `class` is `featured-blog-horizon-section` (NOT `section-featured-blog`), so
//     nothing keyed on the original section class touches it either.
//
// FIX #1 (no nav unless the track actually overflows) — two halves:
//   ≤1 slide: a carousel of ONE card needs no engine and no nav. connectedCallback counts
//     `.swiper-slide`; ≤1 → skip Swiper entirely (the lone card renders statically) and defensively hide
//     any nav the server rendered. The section ALSO gates the arrow DOM on a Liquid slide count, so in
//     the normal path there is nothing to hide — this branch only covers a Liquid/DOM count mismatch.
//   ≥2 slides that FIT: #syncOverflow() measures real overflow (data-* gap/offset + live card widths vs
//     the visible container) after init and on every Swiper resize; when the cards all fit it hides BOTH
//     arrows outright + turns drag off (Swiper's own watchOverflow lock is too strict for a peek carousel
//     with slidesOffsetBefore). This mirrors the ORIGINAL section's featured-blog-carousel.js exactly.
//
// `type="module"` scripts DEFER and run AFTER Motto's classic head scripts. `swiper-bundle.min.js` is a
// classic `defer` head script, so `window.Swiper` is normally ready by the time this body module runs;
// the readiness poll is defence-in-depth (theme-editor re-injection, load-order drift).
//
// UNTRACKED new file (alongside assets/component.js + assets/utilities.js + slideshow-horizon.js) —
// re-verify it exists before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';

/**
 * The featured-blog peek carousel custom element. It is the `.featured-blog` root; `this.refs.swiper`
 * is the `.featured-blog-horizon__swiper.swiper` container Swiper mounts on, `this.refs.navPrev` /
 * `this.refs.navNext` the single circular nav buttons (present only when the section renders ≥2 slides).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} swiper - the `.featured-blog-horizon__swiper.swiper` Swiper mounts on.
 * @property {HTMLElement} [navPrev] - the prev nav button (rendered only for ≥2 slides).
 * @property {HTMLElement} [navNext] - the next nav button (rendered only for ≥2 slides).
 *
 * @extends {Component<Refs>}
 */
export class FeaturedBlogHorizon extends Component {
  /** @type {import('@theme/component') | any} */
  #swiper = null;
  #initialised = false;

  connectedCallback() {
    super.connectedCallback(); // populates this.refs from [ref] descendants
    if (this.#initialised) return;
    this.#whenSwiperReady(() => this.#init());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Swiper `destroy(deleteInstance, cleanStyles)` removes its own listeners + `el.swiper`.
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
    this.#initialised = false;
  }

  /**
   * `window.Swiper` is the vendored classic global loaded via a `defer` head script. It is normally
   * present by the time this deferred module runs; poll briefly (≈5s cap) as a guard for re-injection /
   * load-order drift, then give up silently (the server-rendered cards stay visible either way).
   */
  #whenSwiperReady(callback) {
    if (window.Swiper) {
      callback();
      return;
    }
    let tries = 0;
    const timer = setInterval(() => {
      if (window.Swiper) {
        clearInterval(timer);
        callback();
      } else if (++tries > 100) {
        clearInterval(timer);
      }
    }, 50);
  }

  #init() {
    if (this.#initialised || !this.isConnected) return;
    const swiperEl = this.refs.swiper;
    // Double-init guard: Swiper attaches its instance to the element as `el.swiper`.
    if (!swiperEl || swiperEl.swiper) return;

    // FIX #1 — single-slide: a lone card needs no carousel engine and no nav. Skip Swiper entirely so
    // there is no drag, no arrows, no pagination; the card renders statically (the section adds the
    // `--single` modifier so its left inset still matches the header). Defensive nav-hide covers the
    // rare case where the server rendered arrows despite the Liquid gate (blocks-path count mismatch).
    const slides = swiperEl.querySelectorAll('.swiper-slide');
    if (slides.length <= 1) {
      this.#initialised = true;
      this.refs.navPrev?.style.setProperty('display', 'none');
      this.refs.navNext?.style.setProperty('display', 'none');
      return;
    }

    this.#initialised = true;

    // Peek geometry, read off the element's data-* (Figma: gap 16/32, first-slide offset 24/48). Kept
    // as data-* so the Liquid is the single source and the JS holds Figma fallbacks. Mirrors the
    // constants the original src/js/objects/global/featured-blog-carousel.js hardcodes.
    const ds = this.dataset;
    const gapMobile = parseFloat(ds.gapMobile) || 16;
    const gapDesktop = parseFloat(ds.gapDesktop) || 32;
    const offsetMobile = parseFloat(ds.offsetMobile) || 24;
    const offsetDesktop = parseFloat(ds.offsetDesktop) || 48;
    // Desktop breakpoint = the theme's $grid-desktop-min (currently 1024px). We align the Swiper
    // gap/offset switch to the SAME width the SCSS switches the card width at, so a 312-wide card
    // always pairs with the 32 gap. NB: the original featured-blog-carousel.js still hardcodes 1280
    // (its "mirrors $grid-desktop-min" comment went stale when the token moved 1280 → 1024).
    const desktopMin = parseInt(ds.desktopMin, 10) || 1024;

    const nextEl = this.refs.navNext ?? null;
    const prevEl = this.refs.navPrev ?? null;

    // slidesPerView stays 'auto' at every width (fixed-width slides peek); only the gap + first-slide
    // offset change at desktop. Swiper applies the breakpoint override itself on resize.
    this.#swiper = new window.Swiper(swiperEl, {
      slidesPerView: 'auto',
      spaceBetween: gapMobile,
      slidesOffsetBefore: offsetMobile,
      loop: false,
      watchOverflow: true, // belt-and-braces; #syncOverflow() is the authoritative nav gate
      a11y: { enabled: true },
      navigation:
        nextEl && prevEl
          ? { nextEl, prevEl, disabledClass: 'swiper-button-disabled' }
          : false,
      breakpoints: {
        [desktopMin]: {
          spaceBetween: gapDesktop,
          slidesOffsetBefore: offsetDesktop,
        },
      },
      on: {
        resize: (sw) => this.#syncOverflow(sw),
      },
    });

    this.#syncOverflow(this.#swiper);
  }

  /**
   * FITS-vs-OVERFLOWS gate (FIX #1, ≥2-slide half) — parity with the ORIGINAL section's
   * src/js/objects/global/featured-blog-carousel.js. A PEEK carousel whose cards all FIT the container
   * needs no nav and no drag: "the page already shows all blog post cards" → zero arrows, no swiper
   * affordance. Swiper's own `watchOverflow` lock is TOO STRICT here — with `slidesOffsetBefore` set it
   * only locks when the container clears the content PLUS twice the offset, so a track that visually fits
   * (last card flush to the edge) still reads as "overflowing" and keeps arrows. So measure real overflow
   * from the SAME geometry Swiper lays out with (data-* gap/offset + live card widths) vs the visible
   * container. Runs after init and on every Swiper resize (viewport / breakpoint change flips
   * fits<->overflows). FITS → hide BOTH arrows outright (not just Swiper's greyed disabled state) + drag
   * off; OVERFLOWS → hand nav visibility back to Swiper's disabled-class logic (+ the shared SCSS that
   * hides whichever one is disabled) + drag on. The ≤1-slide case is handled earlier in #init (Swiper is
   * skipped entirely there), so this only runs for ≥2 slides.
   *
   * @param {import('@theme/component') | any} sw - the live Swiper instance.
   */
  #syncOverflow(sw) {
    const swiperEl = this.refs.swiper;
    if (!swiperEl) return;
    const ds = this.dataset;
    const desktopMin = parseInt(ds.desktopMin, 10) || 1024;
    const desktop = window.innerWidth >= desktopMin;
    const gap = desktop
      ? parseFloat(ds.gapDesktop) || 32
      : parseFloat(ds.gapMobile) || 16;
    let content = desktop
      ? parseFloat(ds.offsetDesktop) || 48
      : parseFloat(ds.offsetMobile) || 24; // leading inset (Swiper's slidesOffsetBefore)
    const slides = swiperEl.querySelectorAll('.featured-blog__slide');
    slides.forEach((slide, i) => {
      content += slide.getBoundingClientRect().width;
      if (i < slides.length - 1) content += gap;
    });
    // content = right edge of the last card. If it fits inside the visible container, nothing scrolls.
    const overflows = content > swiperEl.clientWidth + 1; // +1px tolerance for sub-pixel rounding

    sw.allowTouchMove = overflows;
    [this.refs.navPrev, this.refs.navNext].forEach((el) => {
      if (!el) return;
      if (overflows) {
        el.style.removeProperty('display'); // back to Swiper's disabled-class + SCSS control
      } else {
        el.style.setProperty('display', 'none'); // fits → no nav affordance at all
      }
    });
    if (!overflows) sw.slideTo(0, 0); // undo any partial scroll left from a previously-wider viewport
  }
}

if (!customElements.get('featured-blog-horizon')) {
  customElements.define('featured-blog-horizon', FeaturedBlogHorizon);
}
